import { Alert, Button, Inline, Stack, Text } from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import {
	Form,
	SelectField,
	SubmissionError,
	TextInputField,
	GetFieldValue,
	withServerErrors,
} from "@sembark-travel/ui-form"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { SelectWATemplate, TWATemplate } from "../../Integrations"
import { TWhatsAppThread } from "../store"

type TValues = {
	wa_template?: TWATemplate
	template_parameters?: Record<string, string>
}

/**
 * Every named variable the template's body declares. These are what the sender
 * has to supply — nothing resolves them here, since a thread is a conversation
 * rather than a record.
 */
function templateParameterNames(template?: TWATemplate | null): string[] {
	if (!template?.components?.length) {
		return []
	}

	return Array.from(
		new Set(
			template.components.flatMap(
				(component) =>
					component.example?.body_text_named_params?.map(
						(parameter) => parameter.param_name
					) ?? []
			)
		)
	)
}

/**
 * Send an approved template into an existing conversation.
 *
 * Unlike the trip and quote shares there is no record behind this, so every
 * template is offered and every variable is typed by hand. That is also the
 * way back into a conversation whose 24 hour window has closed, where a plain
 * message would be rejected.
 */
export function SendThreadTemplateDialog({
	open,
	onClose,
	thread,
	onSend,
	onSent,
}: {
	open: boolean
	onClose: () => void
	thread: TWhatsAppThread
	onSend: (payload: {
		wa_template_id: number
		template_parameters: Record<string, string>
	}) => Promise<void>
	onSent?: () => void | Promise<void>
}) {
	return (
		<Dialog open={open} onClose={onClose} title="Send a Template" sm>
			<Dialog.Body>
				<Form<TValues>
					initialValues={{ template_parameters: {} }}
					onSubmit={withServerErrors(async (values) => {
						if (!values.wa_template?.id) {
							throw new Error("Please choose a template to send.")
						}
						await onSend({
							wa_template_id: values.wa_template.id,
							template_parameters: values.template_parameters || {},
						})
						showSnackbar("WhatsApp template sent successfully.")
						onClose()
						await onSent?.()
					})}
					subscription={{ submitting: true }}
				>
					{({ handleSubmit, submitting, form }) => (
						<form noValidate onSubmit={handleSubmit}>
							<Stack gap="4">
								<Alert status="info">
									<Text>
										To: {thread.contact?.name || thread.contact?.contact_number}
									</Text>
								</Alert>
								<SelectField
									select={SelectWATemplate}
									name="wa_template"
									label="Template"
									waPhoneNumberId={thread.phone_number?.id}
									fullWidth
									onChange={(option: TWATemplate | undefined) => {
										form.change("wa_template", option)
										// Values belong to the template that declared them.
										form.change("template_parameters", {})
									}}
								/>
								<GetFieldValue<TWATemplate | undefined> name="wa_template">
									{({ value: selected }) => {
										const names = templateParameterNames(selected)

										return names.length ? (
											<Stack gap="3">
												<Text fontWeight="semibold">Template Parameters</Text>
												{names.map((name) => (
													<TextInputField
														key={name}
														name={`template_parameters.${name}`}
														label={name}
														required
													/>
												))}
											</Stack>
										) : null
									}}
								</GetFieldValue>
								<SubmissionError />
								<Inline gap="2">
									<Button type="submit" level="primary" disabled={submitting}>
										{submitting ? "Sending..." : "Send"}
									</Button>
									<Button onClick={onClose} disabled={submitting}>
										Cancel
									</Button>
								</Inline>
							</Stack>
						</form>
					)}
				</Form>
			</Dialog.Body>
		</Dialog>
	)
}
